import { defineStore } from "pinia";
import { type Product, useProductStore } from "./products";
import { productInPackMode, productOrderUnit } from "@/utils/utils.ts";
import { computed, ref } from "vue";
import api from "@/api/axios.ts";
import { useProfileStore } from "@/stores/profile.ts";
import { useToast } from "primevue";
import axios, { type CancelTokenSource } from "axios";
import { useDebounceFn } from "@vueuse/core";

export interface CartItem {
  productId: number;
  unit: string;
  quantity: number;
  price: number; // price per unit, e.g. per kg or per piece
  note: string;
}

export interface Cart {
  items: CartItem[];
  note: string;
  dateTo?: string; // in yyyy-mm-dd format
}

export class ProductNotFound extends Error {
  productId: number;

  constructor(productId: number) {
    super(`Product with ID ${productId} not found in product store.`);
    this.name = "ProductNotFound";
    this.productId = productId;
  }
}

export enum QuantityAdjustResultType {
  NO_ADJUST = "NO_ADJUST",
  ADJUSTED_TO_PACK = "ADJUSTED_TO_PACK",
  ADJUSTED_TO_DECIMAL = "ADJUSTED_TO_DECIMAL",
  ADJUSTED_TO_INTEGER = "ADJUSTED_TO_INTEGER",
  INVALID = "INVALID",
}

class QuantityAdjustResult {
  constructor(
    public type: QuantityAdjustResultType,
    public quantity: number,
  ) {}
}

const QUANTITY_DECIMALS = 2;

export const useCartStore = defineStore("cart", () => {
  const cart = ref<Cart>({ items: [], note: "" }); // Initialize with empty cart to avoid undefined/null errors
  const loading = ref(false);
  const error = ref(false);
  const saveCounter = ref(0); // Counter to track save operations

  let cancelTokenSource: CancelTokenSource | null = null;

  const profileStore = useProfileStore();
  const toast = useToast();

  const cartItems = computed(() => {
    const productStore = useProductStore();
    return cart.value?.items
      .map((item) => {
        const product = productStore.products.find((p) => p.id === item.productId);
        return {
          ...item,
          product,
        };
      })
      .filter((item) => item.product !== undefined);
  });

  const productCount = computed(() => {
    return cart.value?.items.length ?? 0;
  });

  const initialize = async () => {
    loading.value = true;
    error.value = false;
    try {
      const response = await api.get(
        `/custom/gastrofresh/org/${profileStore.selectedOrganizationId}/cart`,
      );
      cart.value = response.data as Cart;
      // make sure cart is in valid format
      if (!cart.value.items) {
        cart.value.items = [];
      }
      if (!cart.value.note) {
        cart.value.note = "";
      }
      if (!cart.value.dateTo) {
        cart.value.dateTo = undefined;
      }
      for (const item of cart.value.items) {
        if (!item.unit) {
          item.unit = "";
        }
        if (!item.price) {
          item.price = 0;
        }
      }
      console.log("<<<<<<<<<< Cart loaded successfully:", cart.value);
    } catch (err) {
      console.error("Error loading cart:", err);
      error.value = true;
    }
    loading.value = false;
  };

  const save = async () => {
    saveCounter.value = saveCounter.value + 1;
    const counter = saveCounter.value;
    console.log(`Saving cart... (counter: ${counter})`);

    // Cancel previous request
    if (cancelTokenSource) {
      console.log(`Cancelling previous save operation (counter: ${counter})`);
      cancelTokenSource.cancel("New save operation");
    }

    const currentCancelSource = axios.CancelToken.source();
    cancelTokenSource = currentCancelSource; // Store reference for next cancellation

    loading.value = true;
    error.value = false;
    try {
      await api.post(
        `/custom/gastrofresh/org/${profileStore.selectedOrganizationId}/cart`,
        cart.value,
        {
          cancelToken: currentCancelSource.token,
        },
      );
      console.log(`********** Cart saved successfully (counter: ${counter})`);
      // toast.add({
      //   severity: "success",
      //   summary: "Košík uložen",
      //   detail: "Košík byl úspěšně uložen.",
      //   life: 3000,
      // });
    } catch (err) {
      if (axios.isCancel(err)) {
        console.warn(`Save operation cancelled (counter: ${counter})`, err.message);
      } else {
        console.error(`Error saving cart (counter: ${counter}):`, err);
        error.value = true;
        toast.add({
          severity: "error",
          summary: "Chyba při ukládání košíku",
          detail: "Došlo k chybě při ukládání košíku. Zkuste to prosím znovu.",
          life: 5000,
        });
      }
    } finally {
      // Only reset if this is still the current operation
      if (cancelTokenSource === currentCancelSource) {
        cancelTokenSource = null;
        loading.value = false;
      }
    }
  };

  const debouncedSave = useDebounceFn(save, 500);

  const getQuantity = (productId: number): number => {
    const item = cart.value?.items.find((item) => item.productId === productId);
    return item ? item.quantity : 0;
  };

  const getUnitPrice = (productId: number): number => {
    const item = cart.value?.items.find((item) => item.productId === productId);
    return item ? item.price : 0;
  };

  const getUnit = (productId: number): string => {
    const item = cart.value?.items.find((item) => item.productId === productId);
    return item ? item.unit : "";
  };

  const getQuantityInBaseUnit = (productId: number): number => {
    const item = cart.value?.items.find((item) => item.productId === productId);
    return item ? item.quantity : 0;
  };

  const getProductTotal = (productId: number): number => {
    const prod = useProductStore().getProductById(productId);
    if (!prod) return 0;
    const q = getQuantityInBaseUnit(productId);
    return q * prod.sellingPrice;
  };

  const getTotalWithoutPortalDiscount = (): number => {
    return (
      cart.value?.items.reduce((total, item) => total + getProductTotal(item.productId), 0) ?? 0
    );
  };

  const getPortalDiscountTotal = (): number => {
    return (
      -(getTotalWithoutPortalDiscount() * (profileStore.selectedOrganizationPortalDiscount ?? 0)) /
      100
    );
  };

  const getTotal = (): number => {
    return getTotalWithoutPortalDiscount() + getPortalDiscountTotal();
  };

  const updateCartQuantityFromInput = async (
    product: Product,
    quantityInput: string,
    dryRun: boolean = false,
  ): Promise<QuantityAdjustResult> => {
    // depending on unit, allow integers or decimals
    const quantityFloat = parseFloat(quantityInput.replace(",", ".").replace(" ", ""));
    if (isNaN(quantityFloat) || quantityFloat < 0) {
      console.log(`Invalid quantity input: "${quantityInput}" for product ID ${product.id}`);
      return new QuantityAdjustResult(QuantityAdjustResultType.INVALID, 0);
    }
    // 3 modes to adjust quantity:
    let quantity;
    let result = QuantityAdjustResultType.NO_ADJUST;
    if (productInPackMode(product)) {
      // If product is in pack mode, adjust to nearest higher pack size
      const packSize = product.unit2Factor ?? 1; // Default to 1 if not defined
      quantity = Math.ceil(quantityFloat / packSize) * packSize;
      if (quantity !== quantityFloat) {
        result = QuantityAdjustResultType.ADJUSTED_TO_PACK;
      }
    } else if (product.unit == "kg") {
      // If product is in kg, adjust to fixed decimal places
      quantity = parseFloat(quantityFloat.toFixed(QUANTITY_DECIMALS));
      if (quantity !== quantityFloat) {
        result = QuantityAdjustResultType.ADJUSTED_TO_DECIMAL;
      }
    } else {
      // For other units, round up to nearest integer
      quantity = Math.ceil(quantityFloat);
      if (quantity !== quantityFloat) {
        result = QuantityAdjustResultType.ADJUSTED_TO_INTEGER;
      }
    }
    console.log(
      `updateCartQuantityFromInput input="${quantityInput}" inputToFloat="${quantityFloat}" adjusted=${quantity} opResult=${result}`,
    );
    if (!dryRun) {
      await updateCartQuantity(product.id, quantity);
      await debouncedSave();
    }
    return new QuantityAdjustResult(result, quantity);
  };

  const updateCartQuantity = async (productId: number, quantity: number) => {
    const index = cart.value.items.findIndex((item) => item.productId === productId);

    if (quantity <= 0) {
      // Remove item if quantity is zero or negative
      if (index >= 0) {
        cart.value?.items.splice(index, 1);
      }
    } else {
      // fix quantity to fixed decimal places (avoid floating point issues such as 0.199999999999999)
      quantity = parseFloat(quantity.toFixed(QUANTITY_DECIMALS));
      // Update quantity if item exists, otherwise add new item
      if (index !== -1) {
        cart.value.items[index].quantity = quantity;
      } else {
        const prod = useProductStore().products.find((p) => p.id === productId);
        if (!prod) {
          console.warn(`Product with ID ${productId} not found in product store.`);
          return;
        }
        cart.value.items.push({
          productId,
          unit: productOrderUnit(prod),
          quantity,
          note: "",
          price: prod.sellingPrice,
        });
      }
    }

    await debouncedSave();
  };

  const clearCart = async () => {
    cart.value.items = [];
    cart.value.note = "";
    cart.value.dateTo = undefined;
    await debouncedSave();
  };

  const updateProductNote = async (productId: number, note: string) => {
    const index = cart.value.items.findIndex((item) => item.productId === productId);
    if (index !== -1) {
      cart.value.items[index].note = note;
      await debouncedSave();
    }
  };

  const getProductNote = (productId: number): string => {
    const item = cart.value?.items.find((item) => item.productId === productId);
    return item?.note || "";
  };

  const updateOrderNote = async (note: string) => {
    cart.value.note = note;
    await debouncedSave();
  };

  const updateDateTo = async (localDateStr: string | undefined) => {
    cart.value.dateTo = localDateStr;
    await debouncedSave();
  };

  return {
    cart,
    loading,
    error,
    cartItems,
    productCount,
    getQuantity,
    getQuantityInBaseUnit,
    getProductTotal,
    getTotal,
    getPortalDiscountTotal,
    updateCartQuantityFromInput,
    updateCartQuantity,
    clearCart,
    updateProductNote,
    getProductNote,
    updateOrderNote,
    initialize,
    updateDateTo,
    getUnit,
    getUnitPrice,
  };
});
